import React, { useState, useEffect, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import DeleteIcon from "../assets/icons/delete-white-icon.svg";
import CloseIcon from "../assets/icons/close-icon.svg";
import { createShortNews, updateShortNews } from "../services/newsService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../components/Loader";

import { IMAGE_BASE_URL } from "../services/imageConfig";

const getLocalDateString = () => {
  const now = new Date();
  const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  return localISO.slice(0, 10);
};

const getTomorrowLocalDateString = () => {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const localISO = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString();
  return localISO.slice(0, 10);
};

const ShortNewsForm = ({ onClose, initialData, mode, onSuccess }) => {
  const [date, setDate] = useState(() => {
    if (mode === "create" && !initialData?.date) {
      return getLocalDateString();
    }
    return initialData?.date || "";
  });

  const [dateOfExpiry, setDateOfExpiry] = useState(() => {
    if (mode === "create" && !initialData?.dateOfExpiry) {
      return getTomorrowLocalDateString();
    }
    return initialData?.dateOfExpiry || "";
  });

  const [title, setTitle] = useState(initialData?.title || "");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const imageInputRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_FILE_SIZE_MB = 10;

  useEffect(() => {
    if (mode === "edit" && initialData?.image) {
      setImage(initialData.image);
      const url = initialData.image.startsWith("https")
        ? initialData.image
        : `${IMAGE_BASE_URL}${initialData.image}`;
      setPreviewUrl(url);
    }
  }, [mode, initialData]);

  useEffect(() => {
    if (!image) {
      setPreviewUrl(null);
      return;
    }
    if (image instanceof File) {
      const objectUrl = URL.createObjectURL(image);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    if (typeof image === "string") {
      setPreviewUrl(
        image.startsWith("https") ? image : `${IMAGE_BASE_URL}${image}`
      );
    }
  }, [image]);

  const isValidFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`Файл "${file.name}" превышает максимальный размер 10MB.`, {
        position: "bottom-left",
        autoClose: 3000,
      });
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFileSize(file)) setImage(file);
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && isValidFileSize(file)) setImage(file);
  };

  const removeImage = () => {
    setImage(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const dataClient = { title, date, dateOfExpiry };
      const formDataToSend = new FormData();
      formDataToSend.append("data_client", JSON.stringify(dataClient));
      formDataToSend.append("isDeleted", "false");

      if (mode === "create") {
        if (!(image instanceof File)) return;
        formDataToSend.append("image_article", image);
      } else if (mode === "edit") {
        if (image instanceof File) {
          formDataToSend.append("image_article", image);
        } else {
          formDataToSend.append("image_article", "null");
        }
      }

      if (mode === "create") {
        await createShortNews(formDataToSend);
        toast.success("Краткая новость успешно создана", {
          position: "bottom-left",
          autoClose: 3000
        });
      } else {
        await updateShortNews(initialData.id, formDataToSend);
        toast.success("Изменения краткой новости сохранены", {
          position: "bottom-left",
          autoClose: 3000
        });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(`Ошибка: ${err.response?.data?.message || err.message}`, {
        position: "bottom-left",
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const isFormValid =
    title.trim() !== "" &&
    date.trim() !== "" &&
    dateOfExpiry.trim() !== "" &&
    image;

  return (
    <div className="modal-overlay">
      <div className="form">
        <div className="form-header">
          <h1 className="form-title">
            {mode === "edit"
              ? "Редактирование краткой новости"
              : "Создание краткой новости"}
          </h1>
          <button className="close-button" onClick={onClose}>
            <img src={CloseIcon} alt="Закрыть" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <h2 className="form-section-title">Информация новости</h2>

          <div className="form-group">
            <label className="field-label">Картинка для новости</label>
            <div
              className="drag-drop-area"
              onClick={() => imageInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleImageDrop}
            >
              {previewUrl ? (
                <div className="image-preview">
                  <img
                    src={previewUrl}
                    alt="Превью"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage();
                    }}
                  >
                    <img src={DeleteIcon} alt="Удалить" />
                  </button>
                </div>
              ) : (
                <span className="placeholder-text">
                  Перетащите или кликните для загрузки картинки
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                onChange={handleImageChange}
                hidden
              />
            </div>
            <label className="field-label max-size-label">Максимальный размер для изображения: 10MB</label>
          </div>

          <div className="form-group">
            <label className="field-label">Дата новости</label>
            <input
              type="date"
              className="date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="field-label">Дата окончания новости</label>
            <input
              type="date"
              className="date-input"
              value={dateOfExpiry}
              onChange={(e) => setDateOfExpiry(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="field-label">Заголовок новости</label>
            <TextareaAutosize
              className="textarea-input"
              placeholder="Введите заголовок новости"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="form-buttons">
            <button type="button" className="cancel-button" onClick={onClose}>
              Отменить
            </button>
            <button type="submit" className="save-button" disabled={!isFormValid}>
              Сохранить
            </button>
          </div>
        </form>
        {isSubmitting && (
          <div className="loader-overlay">
            <Loader />
            <p>Сохранение краткой новости...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortNewsForm;
